// Employees management hooks (payroll employees, separate from staff)
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { queryKeys } from '@/lib/query-client'
import { isAssistantLoggedIn } from '@/lib/permissions'
import type { Employee } from '@/types/entities'

// Extended Employee type with audit display fields
export interface EmployeeWithAudit extends Employee {
  createdByDisplay?: string
  updatedByDisplay?: string
}

/**
 * Fetch all employees for a restaurant
 * Automatically uses assistant RPC if logged in as assistant
 */
export function useEmployees(restaurantId: string | null, includeDeleted = false) {
  return useQuery({
    queryKey: [...queryKeys.accounting.employees(restaurantId || ''), { includeDeleted }],
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    queryFn: async (): Promise<EmployeeWithAudit[]> => {
      if (!restaurantId) {
        return []
      }

      // Check if assistant is logged in
      if (isAssistantLoggedIn()) {
        const { data, error } = await supabase.rpc('assistant_get_employees' as any, {
          p_restaurant_id: restaurantId,
          p_include_deleted: includeDeleted,
        })

        if (error) {
          if (error.message?.includes('PERMISSION_DENIED')) {
            console.warn('Assistant does not have permission to view employees')
            return []
          }
          throw error
        }

        return (data || []).map((e: any) => ({
          id: e.id,
          restaurantId: e.restaurant_id,
          nameAr: e.name_ar,
          nameFr: e.name_fr,
          positionAr: e.position_ar,
          positionFr: e.position_fr,
          monthlySalary: Number(e.monthly_salary),
          hireDate: e.hire_date,
          isActive: e.is_active,
          deletedAt: e.deleted_at,
          createdAt: e.created_at,
          updatedAt: e.updated_at,
          createdByDisplay: e.created_by_display,
          updatedByDisplay: e.updated_by_display,
        }))
      }

      // Admin flow - use regular RPC
      const { data, error } = await supabase.rpc('get_employees', {
        p_restaurant_id: restaurantId,
        p_include_deleted: includeDeleted,
      })

      if (error) {
        throw error
      }

      return (data || []).map((e: any) => ({
        id: e.id,
        restaurantId: e.restaurant_id,
        nameAr: e.name_ar,
        nameFr: e.name_fr,
        positionAr: e.position_ar,
        positionFr: e.position_fr,
        monthlySalary: Number(e.monthly_salary),
        hireDate: e.hire_date,
        isActive: e.is_active,
        deletedAt: e.deleted_at,
        createdAt: e.created_at,
        updatedAt: e.updated_at,
        createdByDisplay: e.created_by_display,
        updatedByDisplay: e.updated_by_display,
      }))
    },
    enabled: !!restaurantId,
  })
}

/**
 * Create a new employee
 * Automatically uses assistant RPC if logged in as assistant
 */
export function useCreateEmployee(restaurantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request: {
      nameAr: string
      nameFr: string
      positionAr?: string
      positionFr?: string
      monthlySalary: number
      hireDate?: string
    }) => {
      // Check if assistant is logged in
      if (isAssistantLoggedIn()) {
        const { data, error } = await supabase.rpc('assistant_create_employee' as any, {
          p_restaurant_id: restaurantId,
          p_name_ar: request.nameAr,
          p_name_fr: request.nameFr,
          p_position_ar: (request.positionAr || null) as any,
          p_position_fr: (request.positionFr || null) as any,
          p_monthly_salary: request.monthlySalary,
          p_hire_date: (request.hireDate || null) as any,
        })

        if (error) {
          throw error
        }

        return data
      }

      // Admin flow
      const { data, error } = await supabase.rpc('create_employee', {
        p_restaurant_id: restaurantId,
        p_name_ar: request.nameAr,
        p_name_fr: request.nameFr,
        p_position_ar: (request.positionAr || null) as any,
        p_position_fr: (request.positionFr || null) as any,
        p_monthly_salary: request.monthlySalary,
        p_hire_date: (request.hireDate || null) as any,
      })

      if (error) {
        throw error
      }

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.accounting.employees(restaurantId),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.accounting.salarySummary(restaurantId),
      })
    },
  })
}

/**
 * Update an existing employee
 * Automatically uses assistant RPC if logged in as assistant
 */
export function useUpdateEmployee(restaurantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request: {
      employeeId: string
      nameAr: string
      nameFr: string
      positionAr?: string
      positionFr?: string
      monthlySalary: number
      hireDate: string
      isActive: boolean
    }) => {
      // Check if assistant is logged in
      if (isAssistantLoggedIn()) {
        const { data, error } = await supabase.rpc('assistant_update_employee' as any, {
          p_restaurant_id: restaurantId,
          p_employee_id: request.employeeId,
          p_name_ar: request.nameAr,
          p_name_fr: request.nameFr,
          p_position_ar: (request.positionAr || null) as any,
          p_position_fr: (request.positionFr || null) as any,
          p_monthly_salary: request.monthlySalary,
          p_hire_date: request.hireDate as any,
          p_is_active: request.isActive,
        })

        if (error) {
          throw error
        }

        return data
      }

      // Admin flow
      const { data, error } = await supabase.rpc('update_employee', {
        p_employee_id: request.employeeId,
        p_name_ar: request.nameAr,
        p_name_fr: request.nameFr,
        p_position_ar: (request.positionAr || null) as any,
        p_position_fr: (request.positionFr || null) as any,
        p_monthly_salary: request.monthlySalary,
        p_hire_date: request.hireDate as any,
        p_is_active: request.isActive,
      })

      if (error) {
        throw error
      }

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.accounting.employees(restaurantId),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.accounting.salarySummary(restaurantId),
      })
    },
  })
}

/**
 * Delete (soft) an employee
 * Automatically uses assistant RPC if logged in as assistant
 */
export function useDeleteEmployee(restaurantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (employeeId: string) => {
      // Check if assistant is logged in
      if (isAssistantLoggedIn()) {
        const { data, error } = await supabase.rpc('assistant_delete_employee' as any, {
          p_restaurant_id: restaurantId,
          p_employee_id: employeeId,
        })

        if (error) {
          throw error
        }

        return data
      }

      // Admin flow
      const { data, error } = await supabase.rpc('delete_employee', {
        p_employee_id: employeeId,
      })

      if (error) {
        throw error
      }

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.accounting.employees(restaurantId),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.accounting.salarySummary(restaurantId),
      })
    },
  })
}

/**
 * Fetch employee edit history
 */
export function useEmployeeEditHistory(restaurantId: string | null, employeeId: string | null) {
  return useQuery({
    queryKey: ['accounting', 'employee-edit-history', restaurantId, employeeId],
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    queryFn: async () => {
      if (!restaurantId || !employeeId) {
        return []
      }

      // Check if assistant is logged in
      if (isAssistantLoggedIn()) {
        const { data, error } = await supabase.rpc('assistant_get_employee_edit_history' as any, {
          p_restaurant_id: restaurantId,
          p_employee_id: employeeId,
        })

        if (error) {
          if (error.message?.includes('PERMISSION_DENIED')) {
            console.warn('Assistant does not have permission to view employee edit history')
            return []
          }
          throw error
        }

        return (data || []).map((h: any) => ({
          id: h.id,
          employeeId: h.employee_id,
          restaurantId: h.restaurant_id,
          fieldChanged: h.field_changed,
          oldValue: h.old_value,
          newValue: h.new_value,
          editedAt: h.edited_at,
          editedBy: h.edited_by,
          editedByAssistantId: h.edited_by_assistant_id,
          createdAt: h.created_at,
          modifiedBy: h.modified_by,
        }))
      }

      // Admin flow
      const { data, error } = await supabase.rpc('get_employee_edit_history' as any, {
        p_employee_id: employeeId,
      })

      if (error) {
        throw error
      }

      return ((data as any[]) || []).map((h: any) => ({
        id: h.id,
        employeeId: h.employee_id,
        restaurantId: h.restaurant_id,
        fieldChanged: h.field_changed,
        oldValue: h.old_value,
        newValue: h.new_value,
        editedAt: h.edited_at,
        editedBy: h.edited_by,
        editedByAssistantId: h.edited_by_assistant_id,
        createdAt: h.created_at,
        modifiedBy: h.modified_by,
      }))
    },
    enabled: !!restaurantId && !!employeeId,
  })
}
